import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import PageLayout from "@/components/feature/PageLayout";
import MediaCard from "@/components/feature/MediaCard";
import FilterBar from "@/pages/category/components/FilterBar";
import { mediaList } from "@/mocks/media";
import type { MediaItem, MediaType } from "@/types/media";
import {
  TYPE_LABEL,
  NAV_CATEGORIES,
  filterAndSort,
  getAllGenres,
  getAllRegions,
  getAllYears,
  isMediaType,
  type FilterOptions,
} from "@/utils/media";

const PAGE_SIZE = 8;

const TYPE_INTRO: Record<MediaType, string> = {
  teleplay: "汇集都市情感、年代励志、古装权谋等热播电视剧集，支持按类型、年份与地区筛选，轻松找到下一部要追的好剧。",
  movie: "院线热映与经典佳片一站浏览，悬疑、爱情、科幻、文艺多元题材，按评分与热度快速挑选今晚要看的电影。",
  variety: "音乐、户外、美食、旅行真人秀应有尽有，看看今天有什么值得开怀大笑或治愈身心的综艺。",
};

const DEFAULT_FILTERS: FilterOptions = { genres: [], years: [], regions: [], sort: "hot" };

export default function CategoryPage() {
  const { type } = useParams();
  const mediaType: MediaType = isMediaType(type) ? type : "teleplay";
  const label = TYPE_LABEL[mediaType];

  const [filters, setFilters] = useState<FilterOptions>(DEFAULT_FILTERS);
  const [visible, setVisible] = useState(PAGE_SIZE);

  const all = mediaList as MediaItem[];
  const scoped = useMemo(() => all.filter((item) => item.type === mediaType), [all, mediaType]);
  const genres = useMemo(() => getAllGenres(all, mediaType), [all, mediaType]);
  const years = useMemo(() => getAllYears(all, mediaType), [all, mediaType]);
  const regions = useMemo(() => getAllRegions(all, mediaType), [all, mediaType]);

  const result = useMemo(() => filterAndSort(scoped, filters), [scoped, filters]);

  useEffect(() => {
    setVisible(PAGE_SIZE);
  }, [filters, mediaType]);

  const shown = result.slice(0, visible);
  const hasMore = visible < result.length;

  const resetFilters = () => setFilters(DEFAULT_FILTERS);

  return (
    <PageLayout
      title={`${label} - 桔子TV影视分类`}
      description={`桔子TV${label}频道，${TYPE_INTRO[mediaType]}`}
    >
      <div className="max-w-[1280px] mx-auto px-4 md:px-8 py-8 md:py-10">
        <nav className="flex items-center gap-1.5 text-xs md:text-sm text-foreground-600 mb-4" aria-label="面包屑">
          <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link>
          <i className="ri-arrow-right-s-line" />
          <span>影视分类</span>
          <i className="ri-arrow-right-s-line" />
          <span className="text-foreground-950">{label}</span>
        </nav>

        <header className="mb-6">
          <h1 className="font-heading text-2xl md:text-4xl font-extrabold text-foreground-950">
            {label}<span className="text-foreground-600 text-base md:text-xl font-medium ml-2">影视分类</span>
          </h1>
          <p className="text-sm text-foreground-600 mt-2.5 max-w-3xl leading-relaxed">{TYPE_INTRO[mediaType]}</p>
        </header>

        <div className="flex items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200 mb-6 w-fit max-w-full overflow-x-auto no-scrollbar">
          {NAV_CATEGORIES.map((item) => (
            <Link
              key={item.type}
              to={`/category/${item.type}`}
              className={`px-4 md:px-5 py-1.5 rounded-full text-xs md:text-sm whitespace-nowrap transition-colors cursor-pointer flex items-center gap-1.5 ${
                item.type === mediaType ? "bg-primary-500 text-background-50 font-medium" : "text-foreground-700 hover:text-foreground-950"
              }`}
            >
              <i className={item.icon} />
              {item.label}
            </Link>
          ))}
        </div>

        <div className="mb-6">
          <FilterBar
            genres={genres}
            years={years}
            regions={regions}
            value={filters}
            onChange={setFilters}
            onReset={resetFilters}
          />
        </div>

        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-foreground-600">
            共找到 <strong className="text-foreground-950 font-semibold">{result.length}</strong> 部{label}
          </p>
        </div>

        {result.length > 0 ? (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
              {shown.map((item) => (
                <MediaCard key={item.id} item={item} />
              ))}
            </div>

            <div className="mt-8 flex justify-center">
              {hasMore ? (
                <button
                  type="button"
                  onClick={() => setVisible((count) => count + PAGE_SIZE)}
                  className="inline-flex items-center gap-2 h-11 px-7 rounded-md bg-background-100 border border-background-300 hover:border-primary-400 text-foreground-950 text-sm font-medium whitespace-nowrap transition-colors cursor-pointer"
                >
                  <i className="ri-add-line" />
                  加载更多（剩余 {result.length - visible} 部）
                </button>
              ) : (
                <p className="text-xs text-foreground-600">已显示全部内容</p>
              )}
            </div>
          </>
        ) : (
          <div className="rounded-lg border border-dashed border-background-300 py-20 text-center">
            <span className="w-16 h-16 mx-auto rounded-full bg-background-200 flex items-center justify-center">
              <i className="ri-file-search-line text-3xl text-foreground-500" />
            </span>
            <h3 className="font-heading text-lg font-semibold text-foreground-950 mt-4">未找到相关影视内容</h3>
            <p className="text-sm text-foreground-600 mt-2">试试调整筛选条件，或重置后重新浏览</p>
            <button
              type="button"
              onClick={resetFilters}
              className="mt-5 inline-flex items-center gap-2 h-10 px-5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium whitespace-nowrap transition-colors cursor-pointer"
            >
              <i className="ri-refresh-line" />
              重置筛选
            </button>
          </div>
        )}
      </div>
    </PageLayout>
  );
}